import { all, call, put, select, takeEvery } from "redux-saga/effects";

import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, pushDataLayerEvent, trackEngagement } from "helpers/analytics";
import { ApiManager } from "services/api";
import ReplyAddressSerializer from "services/api/reply-addresses/reply-address-serializer";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";

import { IEntityCreateFailure, RequestAction } from "../actions";
import { selectTeamsAlphabetically } from "../teams";

import { ReplyAddressActions, ReplyAddressActionsNames } from "./actions";
import {
  ICreateReplyAddressPayload,
  IDeleteReplyAddressPayload,
  IFetchReplyAddressPayload,
  IUpdateReplyAddressPayload,
} from "./interfaces";
import { selectReplyAddressByPrefixDomainPair, selectReplyAddresses } from "./selectors";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchReplyAddress(action: IActionWithPayload<string, IFetchReplyAddressPayload>) {
  const { id } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.replyAddressApi.fetch, id);

  if (response.result) {
    yield put(ReplyAddressActions.fetchSuccess({ id, value: ReplyAddressSerializer.deserialize(response.result) }));
  } else {
    yield put(ReplyAddressActions.fetchFailure({ id, error: response.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchCollection() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.replyAddressApi.fetchAll, {});

  if (response.result) {
    yield put(
      ReplyAddressActions.fetchCollectionSuccess({ values: response.result.map(ReplyAddressSerializer.deserialize) })
    );
  } else {
    yield put(ReplyAddressActions.fetchCollectionFailure({ error: response.error.message }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateReplyAddress(action: IActionWithPayload<string, IUpdateReplyAddressPayload>) {
  const { id, oldDisplayName, displayName, teamIDs } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const teams = yield select(selectTeamsAlphabetically);
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const replyAddresses = yield select(selectReplyAddresses);
  const replyAddress = id ? replyAddresses[id] : null;

  const removedTeamIDs = teams // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((t: any) => (t.replyAddressID || null) === id && t.replyName === oldDisplayName && !teamIDs.includes(t.id)) // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((t: any) => t.id);

  if (removedTeamIDs.length > 0) {
    const fields = {
      replyAddressID: null,
      replyName: null,
    };

    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const removedResponse = yield call(ApiManager.teamApi.batchUpdate, { fields, ids: removedTeamIDs });

    if (!removedResponse.result) {
      yield put(
        ReplyAddressActions.updateFailure({
          // @ts-expect-error TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
          id,
          error: removedResponse.error.message,
          errorObject: removedResponse.error,
        })
      );
      return;
    }
  }

  const fields = {
    replyAddressID: id,
    replyName: displayName,
  };

  const teamsResponse =
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    teamIDs.length > 0 ? yield call(ApiManager.teamApi.batchUpdate, { fields, ids: teamIDs }) : { result: {} };

  if (teamsResponse.result) {
    trackEngagement("Reply addresses: Reply address updated", EventPlace.Settings, {
      ...(id ? { replyAddressID: id } : { defaultAddress: true }),
      oldDisplayName,
      displayName,
      teamIDs,
    });

    yield put(
      ReplyAddressActions.updateSuccess({
        // @ts-expect-error TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        id,
        value: replyAddress,
      })
    );
  } else {
    yield put(
      ReplyAddressActions.updateFailure({
        // @ts-expect-error TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        id,
        error: teamsResponse.error.message,
        errorObject: teamsResponse.error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createReplyAddress(action: IActionWithPayload<string, ICreateReplyAddressPayload>) {
  const { value, displayName, teamIDs } = action.payload;
  const { prefix, emailDomainID } = value || {};
  const existingReplyAddress = value
    ? // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
      yield select(selectReplyAddressByPrefixDomainPair, prefix, emailDomainID)
    : undefined;

  let addressResponse = existingReplyAddress ? { result: { ID: existingReplyAddress.id } } : { result: { ID: null } };
  if (value) {
    addressResponse = yield call(ApiManager.replyAddressApi.create, value);
  }

  if (addressResponse.result) {
    const fields = {
      replyAddressID: addressResponse.result.ID,
      replyName: displayName,
    };

    const teamsResponse =
      // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
      teamIDs.length > 0 ? yield call(ApiManager.teamApi.batchUpdate, { fields, ids: teamIDs }) : { result: {} };

    if (teamsResponse.result) {
      trackEngagement("Reply addresses: Reply address created", EventPlace.Settings, {
        ...(value ? { replyAddressID: addressResponse.result.ID } : { defaultAddress: true }),
        displayName,
        teamIDs,
      });
      pushDataLayerEvent("Reply addresses: Reply address created");
      yield put(
        ReplyAddressActions.createSuccess({
          id: addressResponse.result.ID,
          value: ReplyAddressSerializer.deserialize(addressResponse),
        })
      );
    } else {
      yield put(
        ReplyAddressActions.createFailure({ error: teamsResponse.error.message, errorObject: teamsResponse.error })
      );
    }
  } else {
    yield put(
      ReplyAddressActions.createFailure({
        // @ts-expect-error TS(2339): Property 'error' does not exist on type '{ result:... Remove this comment to see the full error message
        error: addressResponse.error.error.message,
        // @ts-expect-error TS(2339): Property 'error' does not exist on type '{ result:... Remove this comment to see the full error message
        errorObject: addressResponse.error.error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteReplyAddress(action: IActionWithPayload<string, IDeleteReplyAddressPayload>) {
  const { id, displayName } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const teams = yield select(selectTeamsAlphabetically);
  const teamIDs = teams // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter(({ replyAddressID, replyName }: any) => (replyAddressID || null) === id && replyName === displayName)
    // @ts-expect-error TS(7031): Binding element 'teamID' implicitly has an 'any' t... Remove this comment to see the full error message
    .map(({ id: teamID }) => teamID);

  const hasOtherReplyAddresses = teams // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter(({ replyAddressID }: any) => replyAddressID === id) // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .some(({ replyName }: any) => replyName !== displayName);

  const addressResponse =
    id === null || hasOtherReplyAddresses
      ? { result: { ID: null } }
      : // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
        yield call(ApiManager.replyAddressApi.destroy, id);

  if (addressResponse.result) {
    const fields = {
      replyAddressID: null,
      replyName: null,
    };

    const teamsResponse =
      // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
      teamIDs.length > 0 ? yield call(ApiManager.teamApi.batchUpdate, { fields, ids: teamIDs }) : { result: {} };

    if (teamsResponse.result) {
      trackEngagement("Reply addresses: Reply address deleted", EventPlace.Settings, {
        ...(id ? { replyAddressID: id } : { defaultAddress: true }),
        displayName,
      });
      yield put(
        ReplyAddressActions.deleteSuccess({
          id: addressResponse.result.ID,
        })
      );
    } else {
      yield put(ReplyAddressActions.deleteFailure({}));
    }
  } else {
    yield put(ReplyAddressActions.deleteFailure({}));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createReplyAddressSuccess() {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.REPLY_ADDRESS_CREATE_SUCCESS),
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createReplyAddressFailure(action: IActionWithPayload<string, IEntityCreateFailure>) {
  const { type } = action.payload.errorObject || {};
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.REPLY_ADDRESS_CREATE_FAILURE, { type }),
    })
  );
}
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateReplyAddressSuccess() {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.REPLY_ADDRESS_UPDATE_SUCCESS),
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateReplyAddressFailure() {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.REPLY_ADDRESS_UPDATE_FAILURE),
    })
  );
}
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteReplyAddressSuccess() {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.REPLY_ADDRESS_DELETE_SUCCESS),
    })
  );
}
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteReplyAddressFailure() {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.REPLY_ADDRESS_DELETE_FAILURE),
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* replyAddressSagas() {
  yield all([
    takeEvery(ReplyAddressActionsNames.FETCH_COLLECTION[RequestAction.REQUEST], fetchCollection),
    takeEvery(ReplyAddressActionsNames.FETCH_SINGLE[RequestAction.REQUEST], fetchReplyAddress),
    takeEvery(ReplyAddressActionsNames.UPDATE[RequestAction.REQUEST], updateReplyAddress),
    takeEvery(ReplyAddressActionsNames.CREATE[RequestAction.REQUEST], createReplyAddress),
    takeEvery(ReplyAddressActionsNames.DELETE[RequestAction.REQUEST], deleteReplyAddress),
    takeEvery(ReplyAddressActionsNames.CREATE[RequestAction.SUCCESS], createReplyAddressSuccess),
    takeEvery(ReplyAddressActionsNames.UPDATE[RequestAction.SUCCESS], updateReplyAddressSuccess),
    takeEvery(ReplyAddressActionsNames.DELETE[RequestAction.SUCCESS], deleteReplyAddressSuccess),
    takeEvery(ReplyAddressActionsNames.CREATE[RequestAction.FAILURE], createReplyAddressFailure),
    takeEvery(ReplyAddressActionsNames.UPDATE[RequestAction.FAILURE], updateReplyAddressFailure),
    takeEvery(ReplyAddressActionsNames.DELETE[RequestAction.FAILURE], deleteReplyAddressFailure),
  ]);
}
